import React, { useState } from 'react';
import { FaMagic, FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import * as aiService from '../../../influencer/services/aiService';
import './AITextAnalyzer.css';

const AITextAnalyzer = ({ text, onSuggestion, label = 'Message' }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const analyzeText = async () => {
    if (!text || text.trim().length === 0) {
      return;
    }

    setAnalyzing(true);
    setAnalysis(null);
    setSuggestions([]);

    try {
      // Analyze sentiment and get suggestions
      const [sentiment, classification] = await Promise.all([
        aiService.analyzeSentiment(text),
        aiService.classifyText(text),
      ]);

      const analysisResult = {
        sentiment,
        classification,
        recommendations: [],
      };

      // Generate recommendations
      if (sentiment.label === 'negative' && sentiment.score > 0.7) {
        analysisResult.recommendations.push({
          type: 'warning',
          message: 'Message has negative sentiment. Consider making it more positive and friendly.',
        });
      }

      if (text.length < 20) {
        analysisResult.recommendations.push({
          type: 'info',
          message: 'Message is quite short. Consider adding more context or personalization.',
        });
      }

      if (!text.includes('{{name}}') && !text.includes('{{username}}')) {
        analysisResult.recommendations.push({
          type: 'suggestion',
          message: 'Consider adding personalization placeholders like {{name}} or {{username}} for better engagement.',
        });
      }

      if (!text.includes('{{product_link}}')) {
        analysisResult.recommendations.push({
          type: 'suggestion',
          message: 'Add {{product_link}} placeholder to include your product link automatically.',
        });
      }

      setAnalysis(analysisResult);

      // Generate suggestions if callback provided
      if (onSuggestion) {
        const generatedSuggestions = await aiService.generateMessageSuggestions({
          recipientName: 'Customer',
          productName: 'Product',
          tone: 'friendly',
        });
        setSuggestions(generatedSuggestions);
      }
    } catch (error) {
      console.error('Text analysis error:', error);
      setAnalysis({
        sentiment: { label: 'neutral', score: 0.5 },
        classification: { label: 'UNKNOWN', score: 0 },
        recommendations: [
          {
            type: 'error',
            message: 'Analysis failed. Please try again.',
          },
        ],
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const getSentimentColor = (label) => {
    switch (label) {
      case 'positive':
        return 'text-green-600';
      case 'negative':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getSentimentIcon = (label) => {
    switch (label) {
      case 'positive':
        return <FaCheckCircle className="text-green-600" />;
      case 'negative':
        return <FaExclamationTriangle className="text-red-600" />;
      default:
        return null;
    }
  };

  return (
    <div className="ai-text-analyzer">
      <button
        onClick={analyzeText}
        disabled={analyzing || !text || text.trim().length === 0}
        className="ai-analyze-button"
      >
        {analyzing ? (
          <>
            <FaSpinner className="animate-spin" />
            <span>Analyzing...</span>
          </>
        ) : (
          <>
            <FaMagic />
            <span>Analyze {label}</span>
          </>
        )}
      </button>

      {analysis && (
        <div className="ai-analysis-results">
          <div className="ai-sentiment-result">
            <div className="flex items-center space-x-2">
              {getSentimentIcon(analysis.sentiment.label)}
              <span className={`font-semibold ${getSentimentColor(analysis.sentiment.label)}`}>
                Sentiment: {analysis.sentiment.label.toUpperCase()}
              </span>
              <span className="text-sm text-gray-500">
                ({(analysis.sentiment.score * 100).toFixed(1)}% confidence)
              </span>
            </div>
          </div>

          {analysis.recommendations.length > 0 && (
            <div className="ai-recommendations">
              <h4 className="font-semibold mb-2">Recommendations:</h4>
              {analysis.recommendations.map((rec, index) => (
                <div
                  key={index}
                  className={`ai-recommendation ai-recommendation-${rec.type}`}
                >
                  {rec.message}
                </div>
              ))}
            </div>
          )}

          {suggestions.length > 0 && onSuggestion && (
            <div className="ai-suggestions">
              <h4 className="font-semibold mb-2">AI Suggestions:</h4>
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="ai-suggestion-item"
                  onClick={() => onSuggestion(suggestion)}
                >
                  <p>{suggestion}</p>
                  <span className="text-xs text-gray-500">Click to use</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AITextAnalyzer;

